/**
 * 会計ソフト連携設定コンポーネント
 * freee、弥生、マネーフォワードなど主要会計ソフトとの連携
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  AccountBalance,
  CloudSync,
  Check,
  Error as ErrorIcon,
  Settings,
  Link,
  LinkOff,
  History,
  Download,
} from '@mui/icons-material';
import api from '../../services/api';

// 対応会計ソフト
const ACCOUNTING_SOFTWARE = [
  {
    id: 'freee',
    name: 'freee会計',
    logo: '/images/accounting/freee.png',
    description: 'クラウド会計ソフトNo.1。売上データを自動で仕訳',
    features: ['売上自動連携', '仕訳自動生成', 'レポート連携'],
    authType: 'oauth',
  },
  {
    id: 'yayoi',
    name: '弥生会計',
    logo: '/images/accounting/yayoi.png',
    description: '国内シェアNo.1の会計ソフト',
    features: ['CSV連携', '売上データ連携', '仕訳インポート'],
    authType: 'api_key',
  },
  {
    id: 'moneyforward',
    name: 'マネーフォワードクラウド',
    logo: '/images/accounting/moneyforward.png',
    description: 'バックオフィス業務を効率化',
    features: ['API連携', '自動仕訳', 'レポート連携'],
    authType: 'oauth',
  },
  {
    id: 'tkcfx',
    name: 'TKC FXシリーズ',
    logo: '/images/accounting/tkc.png',
    description: '税理士事務所向け会計システム',
    features: ['CSV連携', '仕訳データ連携'],
    authType: 'csv_export',
  },
  {
    id: 'csv',
    name: 'CSV汎用エクスポート',
    logo: null,
    description: 'どの会計ソフトにも対応可能なCSV出力',
    features: ['日次売上', '月次売上', 'カスタムフォーマット'],
    authType: 'none',
  },
];

// 同期頻度
const SYNC_FREQUENCIES = [
  { value: 'realtime', label: 'リアルタイム' },
  { value: 'hourly', label: '1時間ごと' },
  { value: 'daily', label: '毎日（締め後）' },
  { value: 'weekly', label: '毎週' },
  { value: 'manual', label: '手動' },
];

// 勘定科目マッピング
const DEFAULT_ACCOUNT_MAPPING = [
  { posCategory: '現金売上', accountCode: '511', accountName: '売上高' },
  { posCategory: 'クレジット売上', accountCode: '511', accountName: '売上高' },
  { posCategory: '電子マネー売上', accountCode: '511', accountName: '売上高' },
  { posCategory: 'QRコード売上', accountCode: '511', accountName: '売上高' },
  { posCategory: '消費税（10%）', accountCode: '521', accountName: '仮受消費税' },
  { posCategory: '消費税（8%）', accountCode: '521', accountName: '仮受消費税' },
  { posCategory: '決済手数料', accountCode: '633', accountName: '支払手数料' },
];

const AccountingIntegration = () => {
  const { storeId } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [connections, setConnections] = useState({});
  const [selectedSoftware, setSelectedSoftware] = useState(null);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [syncHistory, setSyncHistory] = useState([]);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [accountMapping, setAccountMapping] = useState(DEFAULT_ACCOUNT_MAPPING);

  const [connectionForm, setConnectionForm] = useState({
    apiKey: '',
    clientId: '',
    clientSecret: '',
    syncFrequency: 'daily',
    autoSync: true,
    includeTax: true,
    separateByPaymentMethod: true,
  });

  // 連携状態取得
  const fetchConnections = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/stores/${storeId}/accounting-integrations`);
      setConnections(response.data || {});
    } catch (err) {
      console.error('連携状態の取得に失敗:', err);
      // デモデータ
      setConnections({
        freee: {
          connected: true,
          lastSync: '2026-01-09 06:00:00',
          syncFrequency: 'daily',
          status: 'active',
        },
      });
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  // 同期履歴取得
  const fetchSyncHistory = useCallback(async () => {
    try {
      const response = await api.get(`/api/stores/${storeId}/accounting-sync-history`);
      setSyncHistory(response.data || []);
    } catch (err) {
      // デモデータ
      setSyncHistory([
        {
          id: 1,
          software: 'freee',
          syncedAt: '2026-01-09 06:00:00',
          status: 'success',
          records: 45,
          amount: 234500,
        },
        {
          id: 2,
          software: 'freee',
          syncedAt: '2026-01-08 06:00:00',
          status: 'success',
          records: 52,
          amount: 287300,
        },
        {
          id: 3,
          software: 'freee',
          syncedAt: '2026-01-07 06:00:00',
          status: 'error',
          records: 0,
          error: 'API接続エラー',
        },
      ]);
    }
  }, [storeId]);

  useEffect(() => {
    fetchConnections();
    fetchSyncHistory();
  }, [fetchConnections, fetchSyncHistory]);

  // 連携開始
  const handleStartSetup = (software) => {
    setSelectedSoftware(software);
    setActiveStep(0);
    setConnectionForm({
      apiKey: '',
      clientId: '',
      clientSecret: '',
      syncFrequency: 'daily',
      autoSync: true,
      includeTax: true,
      separateByPaymentMethod: true,
    });
    setSetupDialogOpen(true);
  };

  // OAuth認証開始
  const handleOAuthStart = async () => {
    try {
      const response = await api.post(`/api/stores/${storeId}/accounting/${selectedSoftware.id}/auth`);
      if (response.data.authUrl) {
        window.open(response.data.authUrl, '_blank', 'width=600,height=700');
      }
    } catch (err) {
      setError('認証の開始に失敗しました');
    }
  };

  // 連携保存
  const handleSaveConnection = async () => {
    try {
      setSaving(true);
      setError(null);

      await api.post(`/api/stores/${storeId}/accounting/${selectedSoftware.id}/connect`, {
        ...connectionForm,
        accountMapping,
      });

      setSuccess(true);
      setSetupDialogOpen(false);
      fetchConnections();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('連携の保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // 連携解除
  const handleDisconnect = async (softwareId) => {
    if (!window.confirm('連携を解除しますか？')) return;

    try {
      await api.delete(`/api/stores/${storeId}/accounting/${softwareId}/disconnect`);
      fetchConnections();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('連携の解除に失敗しました');
    }
  };

  // 手動同期
  const handleManualSync = async (softwareId) => {
    try {
      setLoading(true);
      await api.post(`/api/stores/${storeId}/accounting/${softwareId}/sync`);
      fetchSyncHistory();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('同期に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // CSVエクスポート
  const handleCsvExport = async () => {
    try {
      const response = await api.get(`/api/stores/${storeId}/accounting/csv-export`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `accounting_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('CSVエクスポートに失敗しました');
    }
  };

  if (loading && Object.keys(connections).length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountBalance />
        会計ソフト連携
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          操作が完了しました
        </Alert>
      )}

      {/* 対応会計ソフト一覧 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {ACCOUNTING_SOFTWARE.map((software) => {
          const connection = connections[software.id];
          const isConnected = connection?.connected;

          return (
            <Grid item xs={12} sm={6} md={4} key={software.id}>
              <Card
                sx={{
                  height: '100%',
                  border: isConnected ? 2 : 1,
                  borderColor: isConnected ? 'success.main' : 'divider',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {software.logo ? (
                      <Box
                        component="img"
                        src={software.logo}
                        alt={software.name}
                        sx={{ width: 40, height: 40 }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <AccountBalance sx={{ fontSize: 40, color: 'text.secondary' }} />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1">{software.name}</Typography>
                      {isConnected && (
                        <Chip
                          size="small"
                          icon={<Check />}
                          label="連携中"
                          color="success"
                        />
                      )}
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {software.description}
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {software.features.map((feature) => (
                      <Chip key={feature} label={feature} size="small" variant="outlined" />
                    ))}
                  </Box>

                  {isConnected && (
                    <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        最終同期: {connection.lastSync}
                      </Typography>
                    </Box>
                  )}
                </CardContent>

                <CardActions>
                  {isConnected ? (
                    <>
                      <Button
                        size="small"
                        startIcon={<CloudSync />}
                        onClick={() => handleManualSync(software.id)}
                      >
                        今すぐ同期
                      </Button>
                      <Button
                        size="small"
                        startIcon={<Settings />}
                        onClick={() => handleStartSetup(software)}
                      >
                        設定
                      </Button>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDisconnect(software.id)}
                        title="連携解除"
                      >
                        <LinkOff />
                      </IconButton>
                    </>
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<Link />}
                      onClick={() => handleStartSetup(software)}
                    >
                      連携する
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* CSVエクスポート */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle1">CSVエクスポート</Typography>
              <Typography variant="body2" color="text.secondary">
                売上データをCSV形式でダウンロードできます
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleCsvExport}
            >
              CSVダウンロード
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* 勘定科目マッピング */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1">勘定科目マッピング</Typography>
            <Button
              size="small"
              startIcon={<Settings />}
              onClick={() => setMappingDialogOpen(true)}
            >
              編集
            </Button>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>POS区分</TableCell>
                <TableCell>勘定科目コード</TableCell>
                <TableCell>勘定科目名</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accountMapping.slice(0, 4).map((mapping, index) => (
                <TableRow key={index}>
                  <TableCell>{mapping.posCategory}</TableCell>
                  <TableCell>{mapping.accountCode}</TableCell>
                  <TableCell>{mapping.accountName}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {accountMapping.length > 4 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              他 {accountMapping.length - 4} 件の設定
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* 同期履歴 */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <History />
            同期履歴
          </Typography>

          {syncHistory.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              同期履歴がありません
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>日時</TableCell>
                  <TableCell>会計ソフト</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell>件数</TableCell>
                  <TableCell>金額</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {syncHistory.map((history) => (
                  <TableRow key={history.id}>
                    <TableCell>{history.syncedAt}</TableCell>
                    <TableCell>
                      {ACCOUNTING_SOFTWARE.find(s => s.id === history.software)?.name || history.software}
                    </TableCell>
                    <TableCell>
                      {history.status === 'success' ? (
                        <Chip size="small" icon={<Check />} label="成功" color="success" />
                      ) : (
                        <Chip size="small" icon={<ErrorIcon />} label="エラー" color="error" />
                      )}
                    </TableCell>
                    <TableCell>{history.records}件</TableCell>
                    <TableCell>
                      {history.amount ? `¥${history.amount.toLocaleString()}` : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* 連携設定ダイアログ */}
      <Dialog
        open={setupDialogOpen}
        onClose={() => setSetupDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedSoftware?.name}との連携設定
        </DialogTitle>
        <DialogContent>
          {selectedSoftware && (
            <Stepper activeStep={activeStep} orientation="vertical">
              {/* ステップ1: 認証 */}
              <Step>
                <StepLabel>認証</StepLabel>
                <StepContent>
                  {selectedSoftware.authType === 'oauth' ? (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {selectedSoftware.name}のアカウントで認証してください。
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={handleOAuthStart}
                        startIcon={<Link />}
                      >
                        {selectedSoftware.name}で認証
                      </Button>
                    </Box>
                  ) : selectedSoftware.authType === 'api_key' ? (
                    <Box>
                      <TextField
                        fullWidth
                        label="APIキー"
                        value={connectionForm.apiKey}
                        onChange={(e) => setConnectionForm({ ...connectionForm, apiKey: e.target.value })}
                        sx={{ mb: 2 }}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body2">
                      認証は不要です。次のステップに進んでください。
                    </Typography>
                  )}
                  <Box sx={{ mt: 2 }}>
                    <Button onClick={() => setActiveStep(1)}>次へ</Button>
                  </Box>
                </StepContent>
              </Step>

              {/* ステップ2: 同期設定 */}
              <Step>
                <StepLabel>同期設定</StepLabel>
                <StepContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>同期頻度</InputLabel>
                        <Select
                          value={connectionForm.syncFrequency}
                          onChange={(e) => setConnectionForm({ ...connectionForm, syncFrequency: e.target.value })}
                          label="同期頻度"
                        >
                          {SYNC_FREQUENCIES.map((freq) => (
                            <MenuItem key={freq.value} value={freq.value}>
                              {freq.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={connectionForm.autoSync}
                            onChange={(e) => setConnectionForm({ ...connectionForm, autoSync: e.target.checked })}
                          />
                        }
                        label="自動同期を有効にする"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={connectionForm.includeTax}
                            onChange={(e) => setConnectionForm({ ...connectionForm, includeTax: e.target.checked })}
                          />
                        }
                        label="税金情報を含める"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={connectionForm.separateByPaymentMethod}
                            onChange={(e) => setConnectionForm({ ...connectionForm, separateByPaymentMethod: e.target.checked })}
                          />
                        }
                        label="決済方法ごとに仕訳を分ける"
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2 }}>
                    <Button onClick={() => setActiveStep(0)} sx={{ mr: 1 }}>戻る</Button>
                    <Button variant="contained" onClick={() => setActiveStep(2)}>次へ</Button>
                  </Box>
                </StepContent>
              </Step>

              {/* ステップ3: 確認 */}
              <Step>
                <StepLabel>確認</StepLabel>
                <StepContent>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    以下の設定で連携を開始します。
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>会計ソフト</TableCell>
                        <TableCell>{selectedSoftware.name}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>同期頻度</TableCell>
                        <TableCell>
                          {SYNC_FREQUENCIES.find(f => f.value === connectionForm.syncFrequency)?.label}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>自動同期</TableCell>
                        <TableCell>{connectionForm.autoSync ? '有効' : '無効'}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <Box sx={{ mt: 2 }}>
                    <Button onClick={() => setActiveStep(1)} sx={{ mr: 1 }}>戻る</Button>
                    <Button
                      variant="contained"
                      onClick={handleSaveConnection}
                      disabled={saving}
                    >
                      {saving ? <CircularProgress size={20} /> : '連携を開始'}
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            </Stepper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetupDialogOpen(false)}>キャンセル</Button>
        </DialogActions>
      </Dialog>

      {/* 勘定科目マッピングダイアログ */}
      <Dialog
        open={mappingDialogOpen}
        onClose={() => setMappingDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>勘定科目マッピング設定</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            POS売上区分と会計ソフトの勘定科目をマッピングします。
          </Alert>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>POS区分</TableCell>
                <TableCell>勘定科目コード</TableCell>
                <TableCell>勘定科目名</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accountMapping.map((mapping, index) => (
                <TableRow key={index}>
                  <TableCell>{mapping.posCategory}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={mapping.accountCode}
                      onChange={(e) => {
                        const newMapping = [...accountMapping];
                        newMapping[index].accountCode = e.target.value;
                        setAccountMapping(newMapping);
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={mapping.accountName}
                      onChange={(e) => {
                        const newMapping = [...accountMapping];
                        newMapping[index].accountName = e.target.value;
                        setAccountMapping(newMapping);
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMappingDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={() => setMappingDialogOpen(false)}>
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountingIntegration;
